// src/features/campaigns/pages/CampaignsPage.tsx
// ============================================================================
// Re-cableado a lealify-base (Inc10). Las campañas viven en base (/campaigns/*);
// el envío es server-side (worker anti-ban). Esta página lista/crea/edita/archiva;
// programar+monitorear+enviar vive en CampaignPreview/SendPlanModal (Inc10c).
// Fuera: Firestore (colección campaigns, paginación por cursor) y el gate WAHA-navegador.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import { MessageSquare, Plus, Check, Loader2, ChevronLeft as ChevronLeftIcon, DollarSign, Star } from 'lucide-react';
import { Campaign, CampaignFormData, CampaignStatus, CampaignFilterStatus } from '../types/campaign';
// `confirmCancelar` salió de aquí con el handler muerto; sigue exportado en campaignVocab
// (vocabulario compartido) y el aviso de cancelar envío lo pinta CampaignPreview.
import { confirmArchivar, confirmRestaurar } from '../campaignVocab';
import { campaignsApi } from '../../../api/campaignsApi';
import CampaignForm from '../components/CampaignForm';
import CampaignsList from '../components/CampaignsList';
import CampaignPreview from '../components/CampaignPreview';
import ActiveCampaignBanner from '../components/ActiveCampaignBanner';
import PaginationControls from '../../../components/common/PaginationControls';
import { useConfirmDialog } from '../../../components/common/ConfirmDialog';
import { ProLocked } from '../../../components/pro/ProLocked';
import { isPro } from '../../../utils/planPermissions';

const CAMPAIGNS_PER_PAGE = 10;

const CampaignsPageContent: React.FC = () => {
  const { confirm, dialog } = useConfirmDialog();
  const { currentUser } = useAuth();
  const { clearErrors } = useError();
  const contentTopRef = useRef<HTMLDivElement>(null);

  // Todas las campañas del negocio (base devuelve hasta 200; paginamos en cliente).
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [prefillData, setPrefillData] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);

  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [activeFilter, setActiveFilter] = useState<CampaignFilterStatus>('active');

  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  // Imagen adjunta ACTIVADA (2026-07-15): campaignsApi sube el data URI al uploader
  // oficial y base la envía por URL (wa.sendImage la resuelve a base64 hacia WAHA).
  const isPremiumWaha = true;

  // Si llegamos desde el análisis con un "prefill", abrir el form de campaña NUEVA.
  useEffect(() => {
    const pf = (location.state as any)?.prefill;
    if (pf) {
      setPrefillData(pf);
      setEditingCampaign(null);
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCampaigns = useCallback(async () => {
    if (!currentUser?.businessId) { setAllCampaigns([]); setLoading(false); return; }
    setLoading(true);
    try {
      const list = await campaignsApi.list();
      setAllCampaigns(list);
    } catch (e) {
      console.error('Error al obtener campañas:', e);
      setAllCampaigns([]);
    } finally {
      setLoading(false);
      setPageChanging(false);
    }
  }, [currentUser?.businessId]);

  useEffect(() => {
    if (currentUser?.businessId) { if (clearErrors) clearErrors(); loadCampaigns(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.businessId]);

  // Filtro + paginación en cliente.
  // «Activas» incluye también las TERMINADAS de los últimos 30 días (done, no
  // canceladas ni archivadas a mano): una campaña recién enviada es justo la que
  // el dueño entra a revisar — y sus resultados (correo: aperturas/clics) viven
  // en la tarjeta, porque una campaña done no tiene botón de detalle. Sin esto,
  // terminar de enviar la mandaba al cajón de «Archivadas» al instante.
  const DIAS_VISIBLE_TERMINADA = 30;
  const esTerminadaReciente = (c: Campaign): boolean => {
    if (c.motorStatus !== 'done' || c.archivedAt) return false;
    const fin = c.sendPlan?.endDay ? new Date(`${c.sendPlan.endDay}T23:59:59`) : c.lastMessageSentAt;
    if (!fin || isNaN(fin.getTime())) return false;
    return (Date.now() - fin.getTime()) / 86400000 <= DIAS_VISIBLE_TERMINADA;
  };
  const filtered = allCampaigns.filter((c) =>
    activeFilter === 'all' ? true :
    activeFilter === 'active' ? (c.status === 'active' || esTerminadaReciente(c)) :
    c.status === activeFilter);
  const totalCampaigns = filtered.length;
  const totalPages = Math.ceil(totalCampaigns / CAMPAIGNS_PER_PAGE);
  const pageStart = (currentPageNum - 1) * CAMPAIGNS_PER_PAGE;
  const campaigns = filtered.slice(pageStart, pageStart + CAMPAIGNS_PER_PAGE);

  const handleFilterChange = (newFilter: CampaignFilterStatus) => {
    if (newFilter === activeFilter) return;
    setActiveFilter(newFilter);
    setCurrentPageNum(1);
  };

  // ── SUBMIT — crear o actualizar (en base, status draft) ──
  const handleSubmit = async (formData: CampaignFormData) => {
    if (!currentUser?.businessId) return;
    setSaving(true);
    if (clearErrors) clearErrors();
    try {
      if (editingCampaign) {
        await campaignsApi.update(editingCampaign.id, formData);
        setSuccess('Campaña actualizada correctamente');
      } else {
        const id = await campaignsApi.create(formData);
        setSuccess('Campaña creada. Ábrela para programar el envío.');
        void id;
      }
      setShowForm(false);
      setPrefillData(null);
      setEditingCampaign(null);
      await loadCampaigns();
      // Tras crear/editar, abrir el detalle para programar.
      const fresh = editingCampaign ? await campaignsApi.get(editingCampaign.id) : null;
      if (fresh) setPreviewCampaign(fresh);
      setTimeout(() => setSuccess(null), 3500);
    } catch (e: any) {
      console.error('Error al guardar campaña:', e);
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  };

  // Aquí vivía `handleDeleteCampaign` ("eliminar" = cancelar la cola). Estaba MUERTO: se le
  // pasaba a CampaignsList como `onDelete`, prop que ese componente eliminó junto con su
  // botón. Cancelar el envío sigue existiendo, pero en CampaignPreview (el diálogo «Envío
  // programado cancelado», misma llamada `campaignsApi.cancel`).

  // FICHA: archivar / restaurar. Desde F2 el motor tiene `archived_at` y estas dos acciones
  // NO tocan la cola. Antes archivar era `cancel` (descartaba los pendientes) y reactivar era
  // `schedule` (reconstruía la cola desde cero) → el par borraba el historial de envíos y le
  // volvía a escribir a gente que ya había recibido el mensaje. Probado en real el 23-jul-2026.
  const handleChangeCampaignStatus = async (campaignId: string, newStatus: CampaignStatus) => {
    const c = campaigns.find((x) => x.id === campaignId);
    const nombre = c?.name || 'esta campaña';
    if (newStatus === 'archived') {
      const ok = await confirm({
        title: '¿Archivar esta campaña?',
        message: confirmArchivar(nombre, c?.pendingCount || 0),
        confirmLabel: 'Archivar',
      });
      if (!ok) return;
    } else {
      const ok = await confirm({
        title: '¿Restaurar esta campaña?',
        message: confirmRestaurar(nombre),
        confirmLabel: 'Restaurar',
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      if (newStatus === 'archived') {
        await campaignsApi.archive(campaignId);
        setSuccess('Campaña archivada — se conservan sus mensajes enviados y pendientes.');
        if (previewCampaign?.id === campaignId) setPreviewCampaign(null);
      } else {
        await campaignsApi.unarchive(campaignId);
        setSuccess('Campaña restaurada — quedó como estaba, en pausa.');
      }
      await loadCampaigns();
    } catch (e: any) {
      console.error(`Error al cambiar estado de campaña a ${newStatus}:`, e);
      setSuccess(e?.message || 'No se pudo cambiar el estado.');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // ENVÍO: pausar / reanudar. Eje distinto al de archivar; conservan la cola.
  const handlePauseCampaign = async (campaignId: string) => {
    try {
      await campaignsApi.pause(campaignId);
      setSuccess('Envío pausado.');
      await loadCampaigns();
    } catch (e: any) {
      setSuccess(e?.message || 'No se pudo pausar el envío.');
    } finally {
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleResumeCampaign = async (campaignId: string) => {
    try {
      await campaignsApi.resume(campaignId);
      setSuccess('Envío reanudado.');
      await loadCampaigns();
    } catch (e: any) {
      setSuccess(e?.message || 'No se pudo reanudar el envío.');
    } finally {
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleViewCampaignDetails = async (campaignToPreview: Campaign) => {
    if (clearErrors) clearErrors();
    // La lista (listCampaigns) NO trae `message`; pedimos el detalle (getCampaign) para que
    // el preview y el editor muestren el mensaje real. Antes se usaba el ítem de lista → el
    // cuerpo del mensaje salía vacío al ver/editar (bug de visualización, 2026-07-14).
    const full = await campaignsApi.get(campaignToPreview.id).catch(() => null);
    setPreviewCampaign(full ?? campaignToPreview);
    setShowForm(false);
    setEditingCampaign(null);
  };

  const handleEditCampaignFromPreview = (campaignDataFromPreview: Campaign) => {
    if (!previewCampaign) return;
    setEditingCampaign(campaignDataFromPreview);
    setShowForm(true);
  };

  const handleClosePreviewOrForm = () => {
    const wasEditingFromPreview = showForm && editingCampaign && previewCampaign && editingCampaign.id === previewCampaign.id;
    setShowForm(false);
    setEditingCampaign(null);
    setPrefillData(null);
    if (!wasEditingFromPreview) setPreviewCampaign(null);
    loadCampaigns();
    if (clearErrors) clearErrors();
  };

  const handleNextPage = useCallback(() => {
    if (currentPageNum < totalPages) setCurrentPageNum((p) => p + 1);
  }, [currentPageNum, totalPages]);

  const handlePrevPage = useCallback(() => {
    if (currentPageNum > 1) setCurrentPageNum((p) => p - 1);
  }, [currentPageNum]);

  useEffect(() => {
    if (contentTopRef.current && (showForm || previewCampaign)) {
      contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPageNum, showForm, previewCampaign]);

  const isLoadingInitial = loading && allCampaigns.length === 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div ref={contentTopRef}></div>

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
              Campañas de Mensajes
            </h1>
            <div className="mt-1 flex items-center text-sm text-content-2">
              <span>Crea y gestiona campañas</span>
              <span className="mx-2">•</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${loyaltyMode === 'cashback' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'}`}>
                {loyaltyMode === 'cashback' ? (
                  <><DollarSign className="w-3 h-3 mr-1" /> Modo Cashback</>
                ) : (
                  <><Star className="w-3 h-3 mr-1" /> Modo Puntos</>
                )}
              </span>
            </div>
          </div>

          {!showForm && !previewCampaign && (
            <button
              onClick={() => {
                if (clearErrors) clearErrors();
                setShowForm(true);
                setEditingCampaign(null);
                setPrefillData(null);
                setPreviewCampaign(null);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent animate-fade-in-up"
              style={{ animationDelay: '0.15s' }}
            >
              <Plus className="h-4 w-4 mr-2" /> Nueva Campaña
            </button>
          )}
        </div>

        {/* ── Mensaje de éxito ── */}
        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <div className="flex-shrink-0"><Check className="h-5 w-5 text-green-400" /></div>
              <div className="ml-3"><p className="text-sm text-green-700 dark:text-green-300">{success}</p></div>
            </div>
          </div>
        )}

        {/* ── Card principal ── */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">

            {showForm ? (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-content">
                    {editingCampaign ? `Editando: ${editingCampaign.name}` : 'Nueva Campaña'}
                  </h2>
                  {editingCampaign && previewCampaign && editingCampaign.id === previewCampaign.id && (
                    <button
                      onClick={handleClosePreviewOrForm}
                      className="inline-flex items-center px-4 py-2 border border-line rounded-md shadow-sm text-sm font-medium text-content-2 bg-surface hover:bg-surface-2"
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-2" />
                      Cancelar Edición
                    </button>
                  )}
                </div>

                <CampaignForm
                  initialData={editingCampaign ? {
                    name: editingCampaign.name,
                    description: editingCampaign.description,
                    type: editingCampaign.type,
                    mode: editingCampaign.mode,
                    // dispatch faltaba: sin él el form nacía SIEMPRE en 'auto' y al guardar
                    // convertía en automática una campaña asistida (10-sep-2026).
                    dispatch: editingCampaign.dispatch,
                    channel: editingCampaign.channel,
                    emailSubject: editingCampaign.emailSubject,
                    emailDesign: editingCampaign.emailDesign,
                    emailHighlight: editingCampaign.emailHighlight,
                    message: editingCampaign.message,
                    filters: editingCampaign.filters,
                    image: editingCampaign.image ?? null,
                    selectedClientIds: editingCampaign.selectedClientIds,
                  } : (prefillData ?? undefined)}
                  onSubmit={handleSubmit}
                  onCancel={handleClosePreviewOrForm}
                  loading={saving}
                  isPremiumWaha={isPremiumWaha}
                />
              </div>

            ) : previewCampaign ? (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-line">
                  <h2 className="text-xl font-semibold text-content truncate" title={previewCampaign.name}>
                    {previewCampaign.name}
                  </h2>
                  <button
                    onClick={handleClosePreviewOrForm}
                    className="inline-flex items-center px-4 py-2 border border-line rounded-md shadow-sm text-sm font-medium text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent flex-shrink-0"
                  >
                    <ChevronLeftIcon className="h-5 w-5 mr-2" />
                    Volver a Campañas
                  </button>
                </div>
                <CampaignPreview
                  campaign={previewCampaign}
                  loading={saving || loading}
                  onEditMessage={() => handleEditCampaignFromPreview(previewCampaign)}
                />
              </div>

            ) : (
              <>
                <ActiveCampaignBanner variant="page" />
                <div className="mb-4 flex flex-wrap gap-2">
                  {(['active', 'archived', 'all'] as CampaignFilterStatus[]).map(statusValue => (
                    <button
                      key={statusValue}
                      onClick={() => handleFilterChange(statusValue)}
                      disabled={loading}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 disabled:opacity-70
                        ${activeFilter === statusValue
                          ? statusValue === 'all' ? 'bg-blue-600 text-white' :
                            statusValue === 'active' ? 'bg-green-600 text-white' :
                            'bg-gray-500 text-white'
                          : 'bg-surface-2 text-content-2 hover:bg-line'
                        }`}
                    >
                      {statusValue === 'all' && 'Todas'}
                      {statusValue === 'active' && 'Activas'}
                      {statusValue === 'archived' && 'Archivadas'}
                    </button>
                  ))}
                </div>

                <CampaignsList
                  campaigns={campaigns}
                  onEdit={handleViewCampaignDetails}
                  loading={isLoadingInitial}
                  onArchiveCampaign={(campaignId) => handleChangeCampaignStatus(campaignId, 'archived')}
                  onReactivateCampaign={(campaignId) => handleChangeCampaignStatus(campaignId, 'active')}
                  onPauseCampaign={handlePauseCampaign}
                  onResumeCampaign={handleResumeCampaign}
                  currentFilter={activeFilter}
                />

                {totalCampaigns > 0 && totalPages > 1 && !isLoadingInitial && (
                  <PaginationControls
                    currentPage={currentPageNum}
                    onPrevPage={handlePrevPage}
                    onNextPage={handleNextPage}
                    canGoPrev={currentPageNum > 1}
                    canGoNext={currentPageNum < totalPages}
                    isLoading={pageChanging}
                    itemsPerPage={CAMPAIGNS_PER_PAGE}
                    totalItems={totalCampaigns}
                    countOnPage={campaigns.length}
                  />
                )}

                {!loading && campaigns.length === 0 && totalCampaigns === 0 && activeFilter !== 'all' && (
                  <div className="mt-6 pt-6 border-t text-center">
                    <MessageSquare className="h-12 w-12 text-content-3 mx-auto mb-3" />
                    <p className="text-content-3 text-sm">
                      No hay campañas para mostrar con el filtro "{activeFilter === 'active' ? 'Activas' : 'Archivadas'}".
                    </p>
                  </div>
                )}

                {!loading && totalCampaigns === 0 && activeFilter === 'all' && (
                  <div className="mt-6 pt-6 border-t text-center">
                    <MessageSquare className="h-12 w-12 text-content-3 mx-auto mb-3" />
                    <p className="text-content-3 text-sm">Aún no has creado ninguna campaña.</p>
                    <button
                      onClick={() => {
                        if (clearErrors) clearErrors();
                        setShowForm(true);
                        setEditingCampaign(null);
                        setPreviewCampaign(null);
                      }}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Crear Primera Campaña
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {pageChanging && (
        <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
          <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
          <span className="text-sm font-medium text-content-2">Cambiando página...</span>
        </div>
      )}
      {dialog}
    </Layout>
  );
};

// Bloqueo PRO (2-oct-2026, plan pantallas PRO F4): antes un negocio gratis armaba campañas que
// nunca salían. Se decide aquí, antes de montar la página, para no pedir sus datos.
const CampaignsPage: React.FC = () => {
  const { currentUser } = useAuth();
  if (isPro(currentUser?.subscription)) return <CampaignsPageContent />;
  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
            Campañas de Mensajes
          </h1>
          <p className="mt-1 text-sm text-content-2">Crea y gestiona campañas</p>
        </div>
        <ProLocked feature="campaigns" />
      </div>
    </Layout>
  );
};

export default CampaignsPage;
